<template>
  <tile class="opc-addon-config">
    <foldable v-model="isExpanded" :arrow="hasSelectedPlan" :edit="hasSelectedPlan" :title="title">
      <div slot="head" class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h2 class="opc-zm-base-card__name text-capitalize">
            <span :class="[isAdd2CartOrEditFlow ? 'opc-addon-config__title--add' : '']">{{
              title }}</span>
            <i v-if="hasSelectedPlan && !isAdd2CartOrEditFlow" check-icon class="zm-icon-success main-icon" aria-hidden="true" />
          </h2>
          <div v-if="cmrUsedValue.overage > 0 && isExpanded"
            class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <span class="opc-addon-config__subtitle-current">{{ $t('billing.cmr.current_usage') }}:</span>
            <span class="opc-addon-config__subtitle-used">{{ cmrUsedDesc.used }}</span>
            <span class="opc-addon-config__subtitle-overage">{{ cmrUsedDesc.overage }}</span>
            <zm-tooltip class="opc-addon-config-row__input__number--tips"
              :aria-label="`Learn more about ${cmrUsedValue.overage} GB Over capacity`" placement="bottom">
              <div slot="content" v-html="tips" />
              <i role="button" style="margin-left: 4px;" class="zm-icon-info-outline" />
            </zm-tooltip>
          </div>
          <!-- TODO: optimize style -->
          <div v-if="hasSelectedPlan && !isExpanded && isAdd2CartOrEditFlow"
            class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <storage-content :selected-plan="selectedPlan" />
          </div>
          <div v-if="!isAdd2CartOrEditFlow && !isExpanded" slot="collapse"
            class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <storage-content v-if="hasSelectedPlan" :selected-plan="selectedPlan" />
          </div>
        </div>
      </div>
      <div class="divide-line"></div>
      <div class="opc-addon-config__main">
        <p class="opc-addon-config__main__title">{{ desc }}</p>
        <zm-radio-group :value="selectedPlanCode" :enter-select="true" @input="onPlanSelect" ref="radioGroup"
          aria-label="Select a capacity" class="opc-addon-config__main__plan opc-card-radio">
          <zm-radio v-for="plan in visiblePlanList" :key="plan.plan_code" :label="plan.plan_code">
            <p class="opc-addon-config__main__plan__capacity">
              <span>{{ plan.cartCapacity }} {{ plan.cartCapacityUnit }}</span>
              <CycleCurrentBadge :value="plan" />
              <span v-if="recommendedCapacity.category === plan.category"
                class="opc-addon-config__main__plan__capacity-recommend">
                {{ $t('billing.cmr.recommended') }}
              </span>
            </p>
            <p class="opc-addon-config__main__plan__price">
              <span class="opc-addon-config__main__plan__price--amount">{{ $money(plan.amount, { forceDiff: true })
              }}</span>
              <span class="opc-addon-config__main__plan__price--unit">{{
                $t(`billing.common.per_billing_cycle.${plan.billing_cycle}`) }}<priceVATTip />
                </span>
            </p>
          </zm-radio>
        </zm-radio-group>
        <zm-alert v-if="willIncurOverage" class="opc-common-info-alert" type="warning" :closable="false" show-icon>
          <p slot="title">{{ $t('billing.cmr.option_incur_overage') }}</p>
          <p v-html="willIncurOverage"></p>
        </zm-alert>
        <div class="opc-zm-base-card__main__footer">
          <p class="opc-addon-config__main__footer--tips">
            <span>*{{ $t('billing.cmr.overage_fees_desc') + ' ' + $t('billing.cmr.fees_vary_based_on_storage_plan')
            }}</span>
            <span v-for="(plan, index) in visiblePlanList" :key="index">
              {{ plan.cartCapacity }} {{ plan.cartCapacityUnit }}:
              {{ $t('billing.cmr.usage_in_excess', [plan.excessPrice]) }};
            </span>
            <a href="https://support.zoom.us/hc/en-us/articles/360060661511-Cloud-recording-storage-capacity#h_01F4HFK8CMSEBNQZ5J624FHHW7"
              target="_blank">{{ $t('billing.common.Learn_more') }}</a>
          </p>
          <div class="opc-zm-base-card__btn--wrap">
            <zm-button v-if="isShowRemoveBtn" type="link" ghost @click="removePlanByKey"
              :aria-label="'remove ' + title + ' plan'">{{ $t('billing.common.remove') }}</zm-button>
          </div>
        </div>
        <preview-payment class="opc-addon-config__preview-payment" />
      </div>
    </foldable>
  </tile>
</template>

<script>
import { get } from "lodash";
import CMRCommon from './mixin';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import StorageContent from './components/storage-content.vue';
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { planTypes, trackingPlanTypes } from '@/common/constants';
import plansMixin from '@/plans/mx/plan';
import { translation } from '@/plugins/i18n';
import { transferMoney } from '@/utils/money';
import { handleRecommended, getExcessPriceCents, UNLIMITED_CMR } from './constants';
import MessageBox from '@zoom/zoom-ui/src/components/message-box/message-box';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import baseplanFreeToPaidReplenishMixin from "@/components/capacity/mx/freetrial-baseplan-replenish";
import CycleCurrentBadge from '@/components/service/config-billing-cycle/cycle-current-badge.vue';

export default {
  name: 'CMR-Config',
  mixins: [baseplanFreeToPaidReplenishMixin, CMRCommon, plansMixin],
  components: {
    PreviewPayment,
    Foldable,
    Tile,
    StorageContent,
    CycleCurrentBadge,
  },
  data() {
    return {
      isExpanded: true,
      billingCycle: '',
      selectedPlanCode: '',
      planType: planTypes.CMR,
    };
  },
  computed: {
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters(['getSelectedPlans', 'basicConfig', 'cmrUsedValue']),
    planList() {
      return this.planMapFlat.filter((p) => p.type === planTypes.CMR);
    },
    visiblePlanList() {
      return this.planList.filter((p) => p.isVisible === true);
    },
    freeStorage() {
      return this.basicConfig?.freeStorage || '';
    },
    selectedPlan() {
      return this.planList.find((item) => item.plan_code === this.selectedPlanCode);
    },
    cartSelectedPlan() {
      return get(this.getSelectedPlans.filter((p) => p.type === planTypes.CMR), [0]);
    },
    hasSelectedPlan() {
      return !!this.selectedPlanCode;
    },
    desc() {
      if (!!this.freeStorage && this.freeStorage !== UNLIMITED_CMR && this.freeStorage !== '0') {
        if (this.cmrUsedValue.overage > 0) {
          return translation('billing.cmr.add_desc');
        }
        return translation('billing.cmr.account_cloud_storage_desc', [this.freeStorage]) + ' ' + translation('billing.cmr.add_desc');
      } else if (this.freeStorage === UNLIMITED_CMR) {
        return '';
      } else {
        return translation('billing.cmr.config_desc', [5]);
      }
    },
    cmrUsedDesc() {
      const { used, total, overage, overageWithUnit } = this.cmrUsedValue;
      return {
        used: used ? `${used} GB of ${total} GB` : '',
        overage: overage ? `(${overageWithUnit} over capacity)` : ''
      }
    },
    recommendedCapacity() {
      if (this.cmrUsedValue.overage > 0) {
        // find first value to meet this condition or the last one
        return handleRecommended(this.planList, this.cmrUsedValue.overage);
      }
      return {};
    },
    willIncurOverage() {
      if (this.cmrUsedValue.overage > 0 && this.selectedPlan.capacity < this.cmrUsedValue.overage) {
        const overage = this.cmrUsedValue.overage - this.selectedPlan.capacity;
        const money = transferMoney(getExcessPriceCents(this.selectedPlan.excessPriceCents || this.selectedPlan.excessPrice) * overage);
        return this.$t('billing.cmr.selection_will_incur_overage_desc', [`<b style="font-weight: 600">${money}</b>`]);
      }
      return '';
    },
    tips() {
      return this.$t('billing.cmr.account_cloud_storage_include_desc', [`${this.cmrUsedValue.total} GB`]);
    },
  },
  mounted() {
    const freeUpgradeTargetPlan = this.planList.find(p => p.old_plan_code === this.selectedPlanCode)
    if(freeUpgradeTargetPlan){
      if (this.selectedPlanCode !== freeUpgradeTargetPlan.plan_code) {
        this.onPlanSelect(freeUpgradeTargetPlan.plan_code);
        if(this.$query?.upgrade_now !== 'true'){
          let newCapacity = `${freeUpgradeTargetPlan.cartCapacity}${freeUpgradeTargetPlan.cartCapacityUnit}`;
          MessageBox.alert(
            this.$t('billing.cmr.cmr_free_upgrade.update_tip', [newCapacity]),
            this.$t('billing.cmr.cmr_free_upgrade.update_title'),
          );
        }
      }
    }
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlan']),
    ...mapActions(['switchSelectPlan']),
    onPlanSelect(newPlanCode) {
      const oldPlanCode = this.selectedPlanCode;

      if (this.selectedPlanCode !== newPlanCode) {
        if (oldPlanCode) {
          this.switchSelectPlan([oldPlanCode, newPlanCode, undefined]);
        } else {
          this.updateSelectPlan({
            plan_code: newPlanCode,
            cartInfo: { selected_number: 1 },
          });
        }
        this.selectedPlanCode = newPlanCode;
      }

      const plan = this.planList?.find((plan) => plan.plan_code === newPlanCode);
      plan && this.$tracking.addOnBuyInteraction(trackingPlanTypes.CMR, `${plan?.cartCapacity}${plan?.cartCapacityUnit}`);
    },
    onPlanRemove() {
      this.removePlan(this.selectedPlanCode);
      this.selectedPlanCode = '';
    },
    // For A11Y
    getFirstFocusableControl() {
      let firstRadioEl = this.$refs.radioGroup.$el.querySelector('.zm-radio').querySelector('input[type="radio"]');
      return Promise.resolve(firstRadioEl);
    },
    collapseCard() {
      this.isExpanded = false;
    },
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? "";
      },
    },
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction('cloud storage accordion', val ? 'expand' : 'collapse');
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/zoom-card.scss';
@import '@/styles/plans/addon-config.scss';
@import '@/styles/_variables.scss';
@import '@/styles/scss-utils.scss';

.opc-addon-config {
  &__title {
    font-weight: 500;
    font-size: 14px;
    color: #515079;

    i[check-icon] {
      font-size: 16px;
      color: #0B5CFF;
      margin-left: 12px;
    }

    &-content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
    }
  }

  .opc-addon-config__subtitle {
    display: flex;
    align-items: center;

    ul {
      margin-left: 16px;
      display: flex;
      align-items: center;

      li span,
      li label {
        font-size: 14px;
        font-weight: 500;
        line-height: 120%;
        margin-left: 0;
        color: #00053D;
      }

      &>li:not(:first-child)::before {
        content: "•";
        margin: 0 10px;
      }
    }

    &-current {
      color: #6E7680;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      margin-right: 2px;
      text-transform: initial;
    }

    &-used {
      color: #131619;
      margin-left: 0;
      margin-right: 2px;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      text-transform: initial;
    }

    &-overage {
      color: #B36200;
      margin: 0;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      text-transform: initial;
    }
  }

  &__main {
    &__plan {
      display: flex;
      flex-wrap: wrap;
      margin-top: 24px;
      color: #00053D;

      .zm-radio {
        flex: 0 0 calc(50% - 8px);

        &__wrap {
          width: 100%;
        }

        &__label {
          &--inner {
            width: 100%;
          }

          &.is-disabled {

            .opc-addon-config__main__plan__price--amount,
            .opc-addon-config__main__plan__price--unit {
              color: #9191A4;
              @include darkTextDisable;
            }
          }
        }
      }

      &__capacity,
      &__price {
        line-height: 150%;
      }

      &__capacity {
        font-weight: 700;
        font-size: 16px;
        display: flex;
        align-items: center;

        &-recommend {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(96.59deg, #084845 10.36%, #09A639 89.64%);
          border-radius: 100px;
          padding: 0px 8px;
          color: #FFFFFF;
          font-weight: 400;
          font-size: 12px;
          line-height: 16px;
          margin-left: 8px;
        }
      }

      &__price {
        font-weight: 450;
        font-size: 22px;

        &--amount {
          font-size: 22px;
          font-weight: 700;
          color: #00053D;
          @include darkTextColor;
        }

        &--unit {
          font-size: 12px;
          font-weight: 500;
          color: #515079;
          @include darkTextColor2;
        }
      }
    }

    &__footer {
      margin-top: 24px;

      &--tips {
        font-weight: 500;
        font-size: 12px;
        color: #747487;
        text-align: left;
        line-height: 150%;
        @include darkTextColor2;
        @include darkTheme() {
          a {
            color: $dark-blue;
          }
        }
      }
    }
  }

  &__btn {
    &--wrap {
      margin-top: 24px;

      .zm-button {
        padding: 0px;
      }
    }
  }

  .opc-common-info-alert.zm-alert {
    background: #FEF7D9;
    padding: 16px;

    .zm-icon-warning {
      margin-right: 16px;

      &::before {
        content: "";
        background-image: url('../../assets/Alert.svg');
        width: 16px;
        height: 16px;
        background-repeat: no-repeat;
        background-size: cover;
        display: inline-block;
      }
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
